"use client"

import { useEffect, useState } from "react"
import { db } from "../../firebase"
import { collection, getDocs } from "firebase/firestore"
import MobileNav from "../../components/MobileNav"
import { MapPin, Clock, ChevronLeft, ChevronRight } from 'lucide-react'
import "./Events.css"

// Helper function to convert month string to number
const getMonthNumber = (month) => {
  const months = {
    JAN: 0, FEB: 1, MAR: 2, APR: 3, MAY: 4, JUN: 5,
    JUL: 6, AUG: 7, SEP: 8, OCT: 9, NOV: 10, DEC: 11
  }
  return months[month.toUpperCase()] || 0
}

// Helper function to get sortable date value
const getSortableDate = (event) => {
  if (!event.date) return 0
  
  const currentYear = new Date().getFullYear()
  const month = getMonthNumber(event.date.month)
  
  // Handle date ranges (e.g., "27-29")
  let day = event.date.day
  if (day.includes("-")) {
    day = day.split("-")[0] // Use the first day of the range for sorting
  }
  
  // Create a date object for sorting
  return new Date(currentYear, month, parseInt(day, 10)).getTime()
}

export default function Events() {
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const eventsPerPage = 6

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true)
      try {
        const eventsCollection = collection(db, "events")
        const eventSnapshot = await getDocs(eventsCollection)
        const eventList = eventSnapshot.docs.map((doc) => doc.data())
        console.log("Fetched Events:", eventList)
        
        // Sort events by date
        const sortedEvents = [...eventList].sort((a, b) => {
          return getSortableDate(a) - getSortableDate(b)
        })
        
        setEvents(sortedEvents)
      } catch (error) {
        console.error("Error fetching events:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchEvents()
  }, [])

  // Calculate pagination
  const totalPages = Math.ceil(events.length / eventsPerPage)
  const indexOfLastEvent = currentPage * eventsPerPage
  const indexOfFirstEvent = indexOfLastEvent - eventsPerPage
  const currentEvents = events.slice(indexOfFirstEvent, indexOfLastEvent)

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber)
  const nextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages))
  const prevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1))

  return (
    <>
      <header className="light-header">
        <div className="container">
          <div className="logo">
            <a
              href="/"
              style={{
                textDecoration: "none",
                fontWeight: "800",
                fontSize: "1.8rem",
                color: "#2c3e50",
              }}
            >
              Uniquely<span style={{ color: "#c0392b" }}>Sharp</span>
            </a>
          </div>
          <nav className="desktop-nav">
            <a href="/">Home</a>
            <a className="active" href="/events">
              Events
            </a>
            <a href="/products">Products</a>
            <a href="/about">About Us</a>
            <a href="/contact">Contact</a>
          </nav>
          <MobileNav />
        </div>
      </header>

      <div className="hero-section" style={{ backgroundImage: `url('https://i.imgur.com/9ZjBPNq.jpeg')` }}>
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1>Upcoming Events</h1>
          <p>Join us at our exclusive showcases and workshops</p>
        </div>
      </div>

      <section className="events-list">
        <div className="container">
          <h2 className="section-title">Upcoming Events</h2>

          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading events...</p>
            </div>
          ) : events.length === 0 ? (
            <div className="no-events">
              <p>No upcoming events at this time. Check back soon!</p>
            </div>
          ) : (
            <>
              <div className="events-container">
                {currentEvents.map((event, index) => (
                  <div className="event-card" key={index}>
                    <div className="event-header">
                      <div className="event-date">
                        {event.date?.day.includes("-") ? (
                          <>
                            <span className="day-range">{event.date?.day}</span>
                            <span className="month">{event.date?.month}</span>
                          </>
                        ) : (
                          <>
                            <span className="day">{event.date?.day}</span>
                            <span className="month">{event.date?.month}</span>
                          </>
                        )}
                      </div>
                      <div className="event-title">
                        <h3>{event.title}</h3>
                        <p>{event.subtitle || "Learn professional techniques with our expert craftsmen"}</p>
                      </div>
                    </div>

                    <div className="event-meta">
                      <div className="event-location">
                        <MapPin size={18} />
                        <span>{event.location}</span>
                      </div>
                      <div className="event-time">
                        <Clock size={18} />
                        <span>{event.time}</span>
                      </div>
                    </div>

                    <div className="event-description">
                      <p>{event.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button 
                    className="pagination-button" 
                    onClick={prevPage} 
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft size={18} />
                  </button>
                  
                  <div className="pagination-numbers">
                    {[...Array(totalPages)].map((_, i) => (
                      <button
                        key={i}
                        className={`pagination-number ${currentPage === i + 1 ? 'active' : ''}`}
                        onClick={() => paginate(i + 1)}
                      >
                        {i + 1}
                      </button>
                    ))}
                  </div>
                  
                  <button 
                    className="pagination-button" 
                    onClick={nextPage} 
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <footer className="footer-section">
        <div className="footer-content">
          <div className="footer-column">
            <h3>
              Uniquely<span>Sharp</span>
            </h3>
            <p>Premium quality knives and cutting tools for professionals and enthusiasts.</p>
            <div className="social-icons">
              <a href="https://www.facebook.com/share/g/15nBKAPV6a/?mibextid=wwXIfr" aria-label="Facebook">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="icon"
                >
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                </svg>
              </a>
              <a href="https://www.instagram.com/uniquely_sharp" aria-label="Instagram">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="icon"
                >
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                </svg>
              </a>
            </div>
          </div>

          <div className="footer-column">
            <h3>Company</h3>
            <ul>
              <li>
                <a href="/">Home</a>
              </li>
              <li>
                <a href="/events">Events</a>
              </li>
              <li>
                <a href="/products">Products</a>
              </li>
              <li>
                <a href="/about">About Us</a>
              </li>
              <li>
                <a href="/contact">Contact</a>
              </li>
            </ul>
          </div>

          <div className="footer-column">
            <h3>Contact</h3>
            <p>
            Bettendorf, Iowa
            </p>
            <p>
              UniquelySharpContact@gmail.com
              <br />
              (+1) 563-508-2716
            </p>
          </div>
        </div>

        <div className="footer-bottom">
          <p>
            © 2025 <strong>Uniquely Sharp</strong>. All rights reserved.
          </p>
        </div>
      </footer>
    </>
  )
}
